import { isGuid } from "@methods/uniqueMethods";
import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { TaskSeriesDataService } from "@app/tasks/services/task-series-data.service";
import { lastValueFrom, firstValueFrom } from "rxjs";
import { DocumentModel } from "@models/documents/documentmodel";
import { TargetModule } from "@enums/document/TargetModule";
import { TaskSeriesModel } from "@app/tasks/models/task-series-model";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { TabComponentHelper } from "@app/shared/helpers/tab-component-helper";
import { ControlExecutionDataService } from "@app/controls/control-execution/services/control-execution-data.service";
import { TaskSeriesDetailMainComponent } from "./details-main/task-series-detail-main.component";
import { TaskSeriesRecurrenceModel } from "@app/tasks/models/task-series-recurrence-model";
import { RecurrenceFrequency } from "@app/shared/components/cerrix-recurrence/cerrix-recurrence-frequency.enum";
import { RecurrenceViewModel } from "@app/shared/components/cerrix-recurrence/recurrence-view-model";
import { TaskSeriesControlsComponent } from "./task-series-controls/task-series-controls.component";
import { TaskType } from "@app/tasks/enums/TaskType";
import { ControlExecutionPermissionModel } from "@app/controls/control-execution/control-execution-detail/models/control-execution-permission-model";
import { BaseTaskModel } from "@app/tasks/models/BaseTaskModel";
import { TaskLightOverviewModel } from "@app/tasks/models/TaskLightOverviewModel";
import { GenericPaginationListConfig } from "@app/shared/models/GenericPaginationList/GenericPaginationListConfig";
import { FormatType } from "@enums/FormatType";
import { TaskDataService } from "@app/tasks/services/TaskDataService";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { nameof } from "@methods/jeffs-toolkit";
import { provideTranslocoScope, TranslocoService } from "@jsverse/transloco";

@Component({
    selector: "task-series-detail",
    templateUrl: "./task-series-detail.component.html",
    styleUrls: ["./task-series-detail.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope("tasks")],
})
export class TaskSeriesDetailComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private _ds = inject(TaskSeriesDataService);
    private _tds = inject(TaskDataService);
    private _toastr = inject(ToastrService);
    private _promptService = inject(CerrixPromptService);
    private _controlExecutionDataService = inject(ControlExecutionDataService);
    private _tabservice = inject(TabService);
    private _pages = inject(Pages);

    @ViewChild("detailMain") detailMain: TaskSeriesDetailMainComponent;
    @ViewChild("taskseriescontrols") taskseriescontrols: TaskSeriesControlsComponent;

    private _transloco = inject(TranslocoService);

    id: string;
    tab: TabModel;
    model: TaskSeriesModel;
    originalEndDate: Date;
    permissions: ControlExecutionPermissionModel;
    originalModel: TaskSeriesModel;

    promptType = PromptType;

    isInitialized = false;

    //tasks
    tasksConfig: GenericPaginationListConfig;

    //docs
    documents: DocumentModel[];
    isDocumentDirty = false;
    targetModule = TargetModule.Control;

    //hyperlinks
    hyperlinksMenuItemId = "menuItemHyperlinks";
    isHyperlinksDirty = false;

    async ngOnInit(): Promise<void> {
        this.id = this.route.snapshot.params.id ? this.route.snapshot.params.id : this.tab.id;

        await firstValueFrom(this._transloco.load(`tasks/${this._transloco.getActiveLang()}`));

        this.permissions = await lastValueFrom(this._controlExecutionDataService.getPermissions());

        if (!this.permissions.canView) {
            this.tab.close(false);
            return;
        }

        if (this.id) {
            this.model = await firstValueFrom(this._ds.getTaskSeries(this.id));
            this.tab.name = `(TS) ${this.model.name}`;
            this.originalEndDate = this.model.recurrence?.enddate;

            this.model.recurrence.otherByDay = this.model.recurrence.byDay;
            this.model.recurrence.otherByDayType = this.model.recurrence.bySetPos;

            this.createTaskSeriesConfig();
        } else {
            this.model = new TaskSeriesModel();
            this.tab.name = this._transloco.translate("tasks.newTaskSeries");
            if (this.tab.config) {
                const controlGuid = this.tab.config.controlGuid;
                const controlId = await firstValueFrom(
                    this._controlExecutionDataService.getControlId(controlGuid),
                );
                this.model.linkedIds.push(controlId);
                this.model.linkedTypes.push(TaskType.ControlExecution);
            }
        }
        this.originalModel = Object.assign({}, this.model);

        this.isInitialized = true;
        this.tab.showLoader = false;
    }

    async save(): Promise<void> {
        const validatingPrompt = this._promptService.loader(this._transloco.translate("tasks.validatingLoader"));
        this.detailMain.formGroup.markAllAsTouched();
        const recurrenceValidation = this.detailMain.validateRecurrence(); // returns null if all ok

        if (this.detailMain.formGroup.valid && !recurrenceValidation) {
            // check if any control is linked
            validatingPrompt.close();
            if (!this.model.linkedIds.any()) {
                this._toastr.warning(this._transloco.translate("tasks.linkControlWarning"));
                return;
            }

            this.model.recurrence = this.ConvertFromViewModel(this.detailMain.recurrenceViewModel);

            if (
                this.hasEndDateChanged() &&
                !this.checkDayNumberExistenceInRange(
                    this.model.recurrence.startdate,
                    this.model.recurrence.enddate,
                    this.model.recurrence.byMonthDay,
                )
            ) {
                if (!(await this.showPrompt(PromptType.LastDayOfMonth))) {
                    this._toastr.warning(this._transloco.translate("tasks.saveCancelledWarning"));
                    return;
                }
            } else if (this.hasEndDateChanged()) {
                if (!(await this.showPrompt(PromptType.TimeChange))) {
                    this._toastr.warning(this._transloco.translate("tasks.saveCancelledWarning"));
                    return;
                }
            } else {
                if (!(await this.showPrompt(PromptType.GeneralChange))) {
                    this._toastr.warning(this._transloco.translate("tasks.saveCancelledWarning"));
                    return;
                }
            }

            const savingPrompt = this._promptService.loader(this._transloco.translate("tasks.savingLoader"));

            let result: string;
            try {
                result = await firstValueFrom(this._ds.saveTaskSeries(this.model));
            } catch {
                savingPrompt.close();
                return;
            }

            const successMessage = this.model.id > 0
                ? this._transloco.translate("tasks.updateCompleted")
                : this._transloco.translate("tasks.saveSuccessful");
            this._toastr.success(successMessage);

            this.tab.id = result.toString();
            this.tab.refresh();
            savingPrompt.close();
        } else {
            validatingPrompt.close();
            if (this.detailMain.formGroup.valid) {
                this._toastr.warning(recurrenceValidation, this._transloco.translate("tasks.saveFailedTitle"), { enableHtml: true });
                return;
            }

            FormValidationHelper.markAllAsTouched(this.detailMain.formGroup);
            const validationErrors = FormValidationHelper.getFormControlErrors(
                this.detailMain.formGroup,
            );
            const validationMessage = FormValidationHelper.getGeneralErrorMessage(validationErrors);
            this._toastr.warning(validationMessage, this._transloco.translate("tasks.saveFailedTitle"), { enableHtml: true });
        }
    }

    handleMenuItemClick(menuItem: TabMenuItem): void {
        if (menuItem) {
            switch (menuItem.menuItemId) {
                case "menuItemControls":
                    this.taskseriescontrols.load();
                    break;
                case "menuItemDocuments":
                    this.loadDocuments();
                    break;
                case "menuItemHyperlinks":
                    this.loadHyperlinks();
                    break;
            }
        }
    }

    async showPrompt(type: PromptType): Promise<boolean> {
        var title = "";
        var message = "";

        if (type == PromptType.TimeChange) {
            if (!this.model.id || !this.model.recurrence) {
                return true;
            }

            title = this._transloco.translate("tasks.endDateChangedTitle");
            message =
                this.model.recurrence.enddate > this.originalEndDate
                    ? this._transloco.translate("tasks.endDateExtendedMessage")
                    : this._transloco.translate("tasks.endDateShortenedMessage");
        } else if (type == PromptType.LastDayOfMonth) {
            if (!this.model.recurrence) {
                return true;
            }

            title = this._transloco.translate("tasks.lastDayOfMonthTitle");
            message = this._transloco.translate("tasks.lastDayOfMonthMessage", { byMonthDay: this.model.recurrence.byMonthDay });
        } else if (type == PromptType.GeneralChange) {
            if (!this.model.id || !this.model.recurrence || !this.isModelDirty()) {
                return true;
            }

            title = this._transloco.translate("tasks.savingDetailsTitle");
            message = this._transloco.translate("tasks.savingDetailsMessage");
        }

        const promptResult = await firstValueFrom(
            this._promptService
                .prompt({
                    maxWidth: 500,
                    maxHeight: 270,
                    data: {
                        confirmOnEnter: false,
                        closeOnEsc: false,
                        title: title,
                        message: message,
                        confirmButton: {
                            text: this._transloco.translate("tasks.ok"),
                        },
                        cancelButton: {
                            show: true,
                        },
                    },
                })
                .getResult(),
        );
        return promptResult;
    }

    loadDocuments(): void {
        if (!this.documents && this.id && isGuid(this.id.toString())) {
            this._ds.getDocuments(this.id.toString()).subscribe((documents) => {
                this.documents = documents;
                this.model.Documents = documents;
            });
        } else {
            this.documents = [];
        }
    }

    loadHyperlinks(): void {
        if (!this.model.Hyperlinks && this.id && isGuid(this.id.toString())) {
            this._ds.getHyperlinks(this.id.toString()).subscribe((hyperlinks) => {
                this.model.Hyperlinks = hyperlinks;
            });
        }
    }

    checkDocumentsDirty(): void {
        TabComponentHelper.toggleTabDirty(this.tab, "menuItemDocuments", true);
        this.isDocumentDirty = true;
    }

    checkHyperlinksDirty(): void {
        TabComponentHelper.toggleTabDirty(this.tab, this.hyperlinksMenuItemId, true);
        this.isHyperlinksDirty = true;
    }

    private ConvertFromViewModel(
        recurrenceViewModel: RecurrenceViewModel,
    ): TaskSeriesRecurrenceModel {
        let model: TaskSeriesRecurrenceModel = Object.assign(
            {},
            new TaskSeriesRecurrenceModel(),
            recurrenceViewModel,
        );

        if (
            recurrenceViewModel.frequency == RecurrenceFrequency.Monthly ||
            recurrenceViewModel.frequency == RecurrenceFrequency.Yearly
        ) {
            if (recurrenceViewModel.radioSelection == "Other") {
                model.bySetPos = recurrenceViewModel.otherByDayType;
                model.byMonthDay = null;
                model.byDay = recurrenceViewModel.otherByDay;
            } else {
                model.byDay = "";
                model.byMonthDay = recurrenceViewModel.byMonthDay;
                model.otherByDay = null;
                model.otherByDayType = null;
            }
        }
        return model;
    }

    async delete() {
        const deletePrompt = this._promptService.loader(this._transloco.translate("tasks.deletingLoader"));
        try {
            await firstValueFrom(this._ds.deleteTaskSeries(this.id));

            this._toastr.success(this._transloco.translate("tasks.taskSeriesDeleted"));
            this.tab.close(false);
        } finally {
            deletePrompt.close();
        }
    }

    private checkDayNumberExistenceInRange(
        startdate: Date,
        enddate: Date,
        dayNumber: number,
    ): boolean {
        let currentMonth = startdate.getMonth();
        let currentYear = startdate.getFullYear();
        const endMonth = enddate.getMonth();
        const endYear = enddate.getFullYear();

        while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
            if (new Date(currentYear, currentMonth + 1, 0).getDate() < dayNumber) {
                return false;
            }

            currentMonth++;
            if (currentMonth === 12) {
                currentMonth = 0;
                currentYear++;
            }
        }

        return true;
    }

    private hasEndDateChanged(): boolean {
        if (!this.model.recurrence) {
            return false;
        }

        return this.model.recurrence.enddate != this.originalEndDate;
    }

    private createTaskSeriesConfig() {
        if (this.tasksConfig) {
            return;
        }

        this.tasksConfig = <GenericPaginationListConfig>{
            name: this._transloco.translate("tasks.tasksConfigName"),
            idProp: nameof<BaseTaskModel>((x) => x.guid),
            sortProp: nameof<TaskLightOverviewModel>((x) => x.actionDate),
            overviewSortBy: nameof<TaskLightOverviewModel>((x) => x.actionDate),
            uiid: "task-series-tasks-overview",
            isSortableByColumn: true,

            allowEdit: true,

            fields: [
                {
                    fieldName: nameof<TaskLightOverviewModel>((x) => x.identifier),
                    prettyName: this._transloco.translate("tasks.columnIdentifier"),
                    fieldType: GenericListFieldType.Number,
                    headerCssClass: "th-fixed-short",
                },
                {
                    fieldName: nameof<TaskLightOverviewModel>((x) => x.actionDate),
                    prettyName: this._transloco.translate("tasks.columnActionDate"),
                    formatType: FormatType.DateFormat,
                    headerCssClass: "th-fixed-short",
                },
                {
                    fieldName: nameof<TaskLightOverviewModel>((x) => x.responsiblesNames),
                    prettyName: this._transloco.translate("tasks.columnResponsibles"),
                    headerCssClass: "th-fixed-default",
                },
                {
                    fieldName: nameof<TaskLightOverviewModel>((x) => x.reviewerNames),
                    prettyName: this._transloco.translate("tasks.columnReviewers"),
                    headerCssClass: "th-fixed-default",
                },
                {
                    fieldName: nameof<TaskLightOverviewModel>((x) => x.statusDescription),
                    prettyName: this._transloco.translate("tasks.columnStatus"),
                    headerCssClass: "th-fixed-short",
                },
            ],

            dataMethod: () => {
                return this._tds.getAllBySeries(this.model.guid);
            },
            editOverride: (row) => {
                this._tabservice.generateTab(
                    this._pages.TaskDetail,
                    row.guid,
                    `(T) Task ${+row.identifier} - ${row.name}`,
                );
            },
            setAdditionalColumns: (row) => {
                for (const item of row) {
                    switch (item.statusDescription) {
                        case "To do":
                            item.statusDescription_ICON = "fa-circle-xmark";
                            item.statusDescription_COLOR_CLASS = "status-error";
                            break;
                        case "Ready for Review":
                        case "Rejected":
                            item.statusDescription_ICON = "fa-circle-minus";
                            item.statusDescription_COLOR_CLASS = "status-attention";
                            break;
                        case "Done":
                            item.statusDescription_ICON = "fa-circle-check";
                            item.statusDescription_COLOR_CLASS = "status-success";
                            break;
                    }
                }
            },
        };
    }

    // Make TaskType enum available in html
    private isModelDirty(): boolean {
        // Excluding recurrence here because changes there are already handled separately and order might change sometimes resulting in a false dirty
        const { recurrence: _, ...modelWithoutRecurrence } = this.model;
        const { recurrence: __, ...originalModelWithoutRecurrence } = this.originalModel;
        return (
            JSON.stringify(modelWithoutRecurrence) !==
            JSON.stringify(originalModelWithoutRecurrence)
        );
    }

    // Make TaskType enum available in html
    public get taskType(): typeof TaskType {
        return TaskType;
    }
}

enum PromptType {
    TimeChange,
    LastDayOfMonth,
    GeneralChange,
}
